import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import dayjs from 'dayjs';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { genderOptions } from '../../../../constants/app.constants';
import { RequestStateEnum } from '../../../../enums/app.enum';
import { UserGenderEnum } from '../../../../enums/user.enums';
import { CheckInService } from '../../../../services/check-in.service';
import { ModalService } from '../../../../services/modal.service';
import { ToastService } from '../../../../services/toast.service';
import { UserService } from '../../../../services/user.service';
import { User } from '../../../../types/user.types';
import { AlertComponent } from '../../../atoms/alert/alert.component';
import { AlertTypeEnum } from '../../../atoms/alert/alert.types';
import { ButtonComponent } from '../../../atoms/button/button.component';
import { InputComponent } from '../../../atoms/input/input.component';
import { SpinnerComponent } from '../../../atoms/spinner/spinner.component';
import { ModalHeaderComponent } from '../../../molecules/modal/modal-header/modal-header.component';
import { NoEntriesPlaceholderComponent } from '../../../molecules/no-entries-placeholder/no-entries-placeholder.component';
import { RadioGroupWithLabelComponent } from '../../../molecules/radio-group-with-label/radio-group-with-label.component';
import { activityTypeLabels } from '../../activities/activities.constants';
import { Activity } from '../../activities/activities.types';
import { ModalShowQrCodeComponent } from '../modal-show-qr-code/modal-show-qr-code.component';
import { ManualCheckInForm } from './modal-manual-check-in.type';

@Component({
  selector: 'app-modal-manual-check-in',
  imports: [
    ButtonComponent,
    InputComponent,
    MatPaginatorModule,
    ModalHeaderComponent,
    NoEntriesPlaceholderComponent,
    RadioGroupWithLabelComponent,
    MatDialogContent,
    MatDialogActions,
    SpinnerComponent,
    MatCheckboxModule,
    AlertComponent,
  ],
  templateUrl: './modal-manual-check-in.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalManualCheckInComponent implements OnInit, OnDestroy {
  protected AlertTypeEnum = AlertTypeEnum;
  protected RequestStateEnum = RequestStateEnum;
  protected genderOptions = genderOptions;
  static overlayDataType: { activity: Activity };

  private destroy$ = new Subject<void>();

  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly checkInService = inject(CheckInService);
  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);

  alertMsg = '';

  readonly requestState = signal<RequestStateEnum | null>(null);
  readonly selectedUsers = signal<User[]>([]);
  readonly totalItems = signal(0);
  readonly pageSize = signal(5).asReadonly();
  readonly pageIndex = signal(0);
  readonly users = signal<User[]>([]);

  readonly showPaginator = computed(() => this.totalItems() > this.pageSize());

  readonly title = computed(
    () =>
      `${activityTypeLabels[this.data.activity.type]} - ${dayjs(this.data.activity.scheduledDate).format('DD.MM.YYYY')}`
  );

  private readonly resetAlertMsg = effect(() => {
    if (this.requestState() === RequestStateEnum.PENDING) {
      this.alertMsg = '';
    }
  });

  form!: FormGroup<ManualCheckInForm>;

  readonly buttonText = computed(() =>
    this.selectedUsers().length <= 1 ? 'Einchecken' : `Einchecken (${this.selectedUsers().length})`
  );
  readonly activityTypeLabel = computed(() => activityTypeLabels[this.data.activity.type]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: typeof ModalShowQrCodeComponent.overlayDataType
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.searchUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isSelected(id: string): boolean {
    return this.selectedUsers().some((item) => item.id === id);
  }

  public selectUser(user: User): void {
    if (user.isCheckedIn) return;

    this.selectedUsers.update((users) => {
      const exists = users.some((u) => u.id === user.id);
      return exists ? users.filter((u) => u.id !== user.id) : [...users, user];
    });
  }

  public handlePageEvent(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.searchUsers();
  }

  public checkInManually(): void {
    this.requestState.set(RequestStateEnum.PENDING);

    const activity = this.data.activity;
    const userIds = this.selectedUsers().map((item) => item.id);

    this.checkInService.createManualCheckIn(userIds, activity.id, activity.checkInCode).subscribe({
      next: () => {
        this.requestState.set(RequestStateEnum.SUCCESS);
        this.toastService.addToast('Check-In erfolgreich');
        this.modalService.close();
      },
      error: () => {
        this.requestState.set(RequestStateEnum.ERROR);
        this.alertMsg = 'Der Check-In war nicht erfolgreich. Bitte versuche es erneut';
      },
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      email: new FormControl<string | null>(null),
      firstname: new FormControl<string | null>(null),
      lastname: new FormControl<string | null>(null),
      gender: new FormControl<UserGenderEnum | null>(null),
    });

    this.form.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.searchUsers());
  }

  private searchUsers(): void {
    const fc = this.form.controls;
    this.requestState.set(RequestStateEnum.PENDING);

    this.userService
      .search(
        this.pageIndex(),
        this.pageSize(),
        fc.email.value,
        fc.firstname.value,
        fc.lastname.value,
        fc.gender.value,
        this.data.activity.id
      )
      .subscribe({
        next: (response) => {
          this.requestState.set(RequestStateEnum.SUCCESS);
          this.users.set(response.payload.users);
          this.totalItems.set(response.meta.totalItems);
          this.pageIndex.set(response.meta.pageIndex);
        },
        error: () => {
          this.requestState.set(RequestStateEnum.ERROR);
        },
      });
  }
}
